/**
 * Utility functions for calculating and formatting Volume Load (weight * reps).
 *
 * Volume load is unit-agnostic (works identically for lbs or kg without unit suffix).
 * Safe against non-weight/cardio exercises like treadmill, bodyweight, or missing/invalid inputs.
 */
import { parseWeight, parseReps, parseCardioMinutes } from "./numberUtils";

/**
 * Calculates volume load for a single set.
 * Returns 0 if weight or reps are missing, non-numeric, or <= 0.
 *
 * @param {{ weight?: string|number, reps?: string|number } | null | undefined} set
 * @returns {number}
 */
export function calculateSetVolume(set) {
    if (!set || typeof set !== "object") {
        return 0;
    }

    const weight = parseWeight(set.weight);
    const reps = parseReps(set.reps);

    // Treadmill, bodyweight, empty string "", 0, or negative weights produce 0 volume
    if (weight === null || weight <= 0) {
        return 0;
    }

    if (reps === null || reps <= 0) {
        return 0;
    }

    return weight * reps;
}

/**
 * Calculates total volume load for an array of sets.
 *
 * @param {Array<{ weight?: string|number, reps?: string|number }> | null | undefined} sets
 * @returns {number}
 */
export function calculateExerciseVolume(sets) {
    if (!Array.isArray(sets) || sets.length === 0) {
        return 0;
    }

    return sets.reduce((sum, set) => sum + calculateSetVolume(set), 0);
}

/**
 * Calculates total volume load for an entire day across all logged exercises.
 * Day log schema: { [exerciseId]: Array<{ weight?: string|number, reps?: string|number }> }
 *
 * @param {Record<string, Array<{ weight?: string|number, reps?: string|number }>> | null | undefined} dayLog
 * @returns {number}
 */
export function calculateDayVolume(dayLog) {
    if (!dayLog || typeof dayLog !== "object") {
        return 0;
    }

    return Object.values(dayLog).reduce((sum, sets) => sum + calculateExerciseVolume(sets), 0);
}

/**
 * Calculates duration in minutes for a single cardio interval/session.
 * Returns 0 if time is missing, non-numeric, or <= 0.
 *
 * @param {{ time?: string|number } | null | undefined} interval
 * @returns {number}
 */
export function calculateIntervalMinutes(interval) {
    if (!interval || typeof interval !== "object") {
        return 0;
    }

    const minutes = parseCardioMinutes(interval.time);
    if (minutes === null || minutes <= 0) {
        return 0;
    }

    return minutes;
}

/**
 * Calculates total cardio minutes for an array of intervals/sets.
 *
 * @param {Array<{ time?: string|number }> | null | undefined} intervals
 * @returns {number}
 */
export function calculateExerciseCardioMinutes(intervals) {
    if (!Array.isArray(intervals) || intervals.length === 0) {
        return 0;
    }

    return intervals.reduce((sum, item) => sum + calculateIntervalMinutes(item), 0);
}

/**
 * Calculates total cardio minutes across all logged exercises for an entire day.
 *
 * @param {Record<string, Array<{ time?: string|number }>> | null | undefined} dayLog
 * @returns {number}
 */
export function calculateDayCardioMinutes(dayLog) {
    if (!dayLog || typeof dayLog !== "object") {
        return 0;
    }

    return Object.values(dayLog).reduce((sum, intervals) => sum + calculateExerciseCardioMinutes(intervals), 0);
}

/**
 * Formats a numeric volume load without units.
 * Uses locale-specific number grouping (e.g., "1,250" in en, "1 250" in uk).
 *
 * @param {number} volume
 * @param {string} [locale="en-US"]
 * @param {object} [options]
 * @param {string} [options.emptyPlaceholder="—"] Placeholder when volume is 0 or invalid
 * @returns {string}
 */
export function formatVolumeLoad(volume, locale = "en-US", options = {}) {
    const { emptyPlaceholder = "—" } = options;

    if (!Number.isFinite(volume) || volume <= 0) {
        return emptyPlaceholder;
    }

    const rounded = Math.round(volume * 10) / 10;
    try {
        return new Intl.NumberFormat(locale || "en-US", {
            maximumFractionDigits: 1,
        }).format(rounded);
    } catch (_) {
        return rounded.toLocaleString();
    }
}

/**
 * Formats dual metrics: volume load (tonnage) and cardio duration.
 *
 * @param {number} volume
 * @param {number} cardioMinutes
 * @param {string} [locale="en-US"]
 * @param {object} [options]
 * @param {string} [options.emptyPlaceholder="—"]
 * @param {string} [options.cardioUnit] Defaults to "min" for en, "хв" for uk
 * @returns {string}
 */
export function formatDualVolume(volume, cardioMinutes, locale = "en-US", options = {}) {
    const { emptyPlaceholder = "—" } = options;
    const isUk = String(locale).toLowerCase().startsWith("uk");
    const unit = options.cardioUnit || (isUk ? "хв" : "min");

    const hasVolume = Number.isFinite(volume) && volume > 0;
    const hasCardio = Number.isFinite(cardioMinutes) && cardioMinutes > 0;

    if (hasVolume && hasCardio) {
        const roundedCardio = Math.round(cardioMinutes * 10) / 10;
        return `${formatVolumeLoad(volume, locale)} • 🏃 ${roundedCardio} ${unit}`;
    }

    if (hasVolume) {
        return formatVolumeLoad(volume, locale);
    }

    if (hasCardio) {
        const roundedCardio = Math.round(cardioMinutes * 10) / 10;
        return `🏃 ${roundedCardio} ${unit}`;
    }

    return emptyPlaceholder;
}

